<script>
import dayjs from 'dayjs';
import { formatNumber } from '@ubic/utils/formatters';
import IconClock from '@/components/icons/Clock';
import IconGhost from '@/components/icons/Ghost';
import WithCSSTooltip from '@/components/WithCSSTooltip';
import DateRangeSelect from '@/components/DateRangeSelect';

import { colorsProjects } from '@/constants';

export default {
  props: {
    getDataService: {
      type: Function,
      required: true,
    },
  },
  data () {
    return {
      isFetching: false,
      data: [],

      filter: {
        minDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
        maxDate: dayjs().format('YYYY-MM-DD'),
      },
    };
  },
  methods: {
    getLinkWithFilter (path) {
      return {
        path,
        query: this.filter,
      };
    },
    async handleDateRangeChange (dates) {
      this.filter = {
        ...this.filter,
        ...dates,
      };

      this.$router.replace({
        ...this.$route,
        query: {
          ...this.$route.query,
          workersFilter: `${dates.minDate},${dates.maxDate}`,
        },
      });

      this.data = await this.getDataService(this.filter);
    },
  },
  async created () {
    this.isFetching = true;

    if (this.$route.query.workersFilter) {
      const [minDate, maxDate] = this.$route.query.workersFilter.split(',');

      this.filter = {
        ...this.filter,
        minDate,
        maxDate,
      };
    }

    this.data = await this.getDataService(this.filter);

    this.isFetching = false;
  },
  render () {
    if (this.isFetching) {
      return <u-spinner />;
    }

    const { $style, getLinkWithFilter } = this;

    return (
      <div>
        <header class="page-header">
          <u-title level="2">
            Активные сотрудники
          </u-title>

          <DateRangeSelect
            value={this.filter}
            onChange={this.handleDateRangeChange}
          />
        </header>

        <div class={$style.list}>
          {this.data.map(worker => worker.track && (
            <div
              key={worker.id}
              class={$style.worker}
            >
              <div class={$style.workerInfo}>
                <span class={$style.workerInfoName}>
                  <WithCSSTooltip>
                    <span
                      slot="tooltip"
                      class={$style.workerDepartmentsItemTooltip}
                    >
                      {worker.department}
                    </span>
                    <span
                      class={$style.workerDepartmentColor}
                      style={{ color: worker.color }}
                    />
                  </WithCSSTooltip>
                  <router-link
                    to={getLinkWithFilter(`/statistics/worker/${worker.id}`)}
                  >
                    {worker.name}
                  </router-link>
                </span>
                <span class={$style.workerInfoHours}>
                  <IconClock class={$style.workerInfoIcon} />
                  {formatNumber(Math.round(worker.totalHours))}
                </span>
              </div>

              <div class={$style.workerDepartments}>
                {worker.projects.map((project, index) => {
                  const width = project.totalHours / worker.totalHours;

                  return (
                    <WithCSSTooltip
                      key={project.name}
                      class={$style.workerDepartmentsItem}
                      style={{
                        color: colorsProjects[index],
                        width: `${100 * width}%`,
                      }}
                    >
                      <span
                        slot="tooltip"
                        class={$style.workerDepartmentsItemTooltip}
                      >
                        {project.name}
                        {' '}
                        <span class={$style.workerInfoHours}>
                          <IconClock class={$style.workerInfoIcon} />
                          {formatNumber(Math.round(project.totalHours))}
                        </span>
                      </span>
                    </WithCSSTooltip>
                  );
                })}
              </div>
            </div>
          ))}

          {!this.data.length && (
            <div class={$style.listEmpty}>
              <div class={$style.listEmptyIcon}>
                <IconGhost />
              </div>
              Нет активности за выбранный период
            </div>
          )}
        </div>
      </div>
    );
  },
};
</script>

<style lang="scss" module>
@import '@/theme/include.scss';

.list {
  display: grid;
  grid-column-gap: 1em;
  grid-row-gap: 1em;
  grid-template-columns: 1fr 1fr 1fr;
  margin: 0 -1em;

  &Empty {
    align-items: center;
    color: $color-gray;
    display: flex;
    flex-direction: column;
    grid-column: 1 / span 3;
    justify-content: center;
    padding: 32px;
    text-align: center;

    &Icon {
      color: $color-gray-lighter;
      font-size: 5em;
      margin: .125em 0;
    }
  }
}

.worker {
  background-color: $color-white;
  border: 1px solid $color-gray-lighter;
  border-radius: .25em;
  display: block;
  padding: .75em;

  &Info {
    align-items: center;
    display: flex;

    &Icon {
      font-size: .75em;
      margin-right: .5em;
    }

    &Name {
      margin-right: auto;
    }

    &Workers {
      margin-left: 1em;
    }

    &Hours {
      margin-left: 1em;
    }
  }

  &Departments {
    align-items: stretch;
    display: flex;
    height: 1em;
    justify-content: stretch;
    margin-top: .5em;

    &:empty {
      background-color: $color-gray-lighter;
      border-radius: .25em;
    }

    &Item {
      background-color: currentColor;
      flex-grow: 0;
      flex-shrink: 1;
      min-width: .5em;
      transition: width .2s;

      &:first-child {
        border-bottom-left-radius: .25em;
        border-top-left-radius: .25em;
      }

      &:last-child {
        border-bottom-right-radius: .25em;
        border-top-right-radius: .25em;
      }

      &Tooltip {
        color: $color-black;
        font-size: .825em;
        white-space: nowrap;
      }
    }
  }

  &DepartmentColor {
    background-color: currentColor;
    border-radius: 50%;
    display: inline-block;
    font-size: .75rem;
    height: 1em;
    margin-right: .5em;
    width: 1em;
  }
}
</style>
